import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';

import { HostState } from './types';
import { OwnerIdParam } from 'shared/types';
import { fetchHosts, resetHosts } from 'store/hosts/actions';
import { hostsState as hostsStateSelector } from './selectors';
import { useParams } from 'react-router-dom';

const DEFAULT_LIMIT = 100;

export const useHostsState = (): HostState => {
  const { ownerId } = useParams<OwnerIdParam>();
  const dispatch = useDispatch();
  const hostsState = useSelector(hostsStateSelector);

  useEffect(() => {
    if (!hostsState.data) {
      dispatch(fetchHosts.request({ ownerId, limit: DEFAULT_LIMIT, offset: 0 }));
    }
  }, [dispatch, hostsState.data, ownerId]);

  return hostsState;
};

interface UsePaginatedHostsReturn extends HostState {
  hasMore: boolean;
  loadMore: () => void;
  search: (query: string) => void;
  reset: () => void;
}

export const usePaginatedHosts = (): UsePaginatedHostsReturn => {
  const { ownerId } = useParams<OwnerIdParam>();
  const dispatch = useDispatch();
  const hostsState = useSelector(hostsStateSelector);
  const loadingRef = useRef(false);

  // Sync loading state to ref
  useEffect(() => {
    loadingRef.current = hostsState.loading || hostsState.loadingMore;
  }, [hostsState.loading, hostsState.loadingMore]);

  // Initial fetch on mount
  useEffect(() => {
    dispatch(fetchHosts.request({ ownerId, limit: DEFAULT_LIMIT, offset: 0 }));
  }, [dispatch, ownerId]);

  const hasMore = hostsState.total !== null && hostsState.offset < hostsState.total;

  const loadMore = useCallback(() => {
    if (!ownerId || loadingRef.current || !hasMore) {
      return;
    }
    dispatch(
      fetchHosts.request({
        ownerId,
        query: hostsState.query || undefined,
        limit: DEFAULT_LIMIT,
        offset: hostsState.offset,
      }),
    );
  }, [dispatch, ownerId, hasMore, hostsState.query, hostsState.offset]);

  const search = useCallback(
    (query: string) => {
      if (!ownerId) {
        return;
      }
      // Reset and search with new query
      dispatch(
        fetchHosts.request({
          ownerId,
          query: query || undefined,
          limit: DEFAULT_LIMIT,
          offset: 0,
        }),
      );
    },
    [dispatch, ownerId],
  );

  const reset = useCallback(() => {
    dispatch(resetHosts());
    if (ownerId) {
      dispatch(fetchHosts.request({ ownerId, limit: DEFAULT_LIMIT, offset: 0 }));
    }
  }, [dispatch, ownerId]);

  return {
    ...hostsState,
    hasMore,
    loadMore,
    search,
    reset,
  };
};
